import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
  AddRentLineDto,
  CreateRentDto,
  RemoveEquipmentDto,
  RentResponse,
  UpdateEquipmentPaymentValue,
  UpdateRentCustomerOrAddressDto,
  UpdateRentDateDto,
} from '../../../api/client/data-contracts';
import { Rents } from '../../../api/client/Rents';
import {
  QueriedData,
  showErrorNotification,
  showSuccessNotification,
} from '../assets/useAssets';
import dayjs, { Dayjs } from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { defaultApiConfig } from '../../../api';

dayjs.extend(isBetween);

type RentCommandArg<T> = { rentId: string; dto: T };
type AddRentEquipmentArg = { rentId: string; dto: AddRentLineDto };
type RemoveEquipmentArg = { rentId: string; dto: RemoveEquipmentDto };

export interface UseRentsResult {
  rentsQuery: QueriedData<RentResponse[]>;
  createRent: (createRentDto: CreateRentDto) => void;
  startRent: (rentId: string) => void;
  reserveRent: (rentId: string) => void;
  cancelRent: (rentId: string) => void;
  endRent: (rentId: string) => void;
  markRentAsPayed: (rentId: string) => void;
  markRentAsUnpayed: (rentId: string) => void;
  addRentEquipment: (data: AddRentEquipmentArg) => void;
  removeEquipment: (data: RemoveEquipmentArg) => void;
  updateRentDate: (data: RentCommandArg<UpdateRentDateDto>) => void;
  updateRentCustomerOrAddress: (
    data: RentCommandArg<UpdateRentCustomerOrAddressDto>,
  ) => void;
  openRent: (rentId: string) => void;
  updateRentEquipmentPaymentValue: (
    data: RentCommandArg<UpdateEquipmentPaymentValue>,
  ) => void;

  bulkDeleteRents: (ids: string[]) => void;
}

const rentsApi = new Rents(defaultApiConfig);
export interface ReadRentsQuery {
  state?: RentResponse['state'];
  startDateInterval?: { begin: string; end: string };
  asset?: string;
  model?: string;
  brand?: string;
  tags?: string[];
}

export function useRents(query?: ReadRentsQuery): UseRentsResult {
  const queryKey = ['rents', JSON.stringify(query)];

  let queryParams = query;
  if (!query || Object.entries(query).length === 0) {
    queryParams = undefined;
  }

  const rentsQuery = useQuery(queryKey, () =>
    rentsApi
      .findRents(queryParams || {})
      .then((res) => res.data.filter((r) => r.state !== 'DELETED')),
  );

  const queryClient = useQueryClient();
  const intl = useIntl();
  const history = useHistory();
  const { mutate: createRent } = useMutation(
    (createRentDto: CreateRentDto) =>
      rentsApi.createRent(createRentDto).then((res) => res.data),
    {
      onSuccess: (data) => {
        showSuccessNotification('rent-create-success-message', intl);
        queryClient.invalidateQueries(queryKey);
        history.push(`/apps/cockpit/rents/${(data as any).id}`);
      },
      onError: (error) => {
        showErrorNotification('rent-create-fail-message', intl);
        console.log(error);
      },
    },
  );

  const { mutate: startRent } = useMutation(
    (rentId: string) => rentsApi.startRent(rentId).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('rent-start-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error) => {
        showErrorNotification('rent-start-fail-messsage', intl);
        console.log(error);
      },
    },
  );

  const { mutate: reserveRent } = useMutation(
    (rentId: string) => rentsApi.reserveRent(rentId).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('rent-reserve-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error) => {
        showErrorNotification('rent-reserve-fail-messsage', intl);
        console.log(error);
      },
    },
  );

  const { mutate: endRent } = useMutation(
    (rentId: string) => rentsApi.endRent(rentId).then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        showSuccessNotification('rent-end-success-message', intl);
      },
      onError: (error) => {
        showErrorNotification('rent-end-fail-message', intl);
        console.log(error);
      },
    },
  );

  const { mutate: cancelRent } = useMutation(
    (rentId: string) => rentsApi.cancelRent(rentId).then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        showSuccessNotification('Aluguer cancelado com sucesso', intl);
      },
      onError: (error) => {
        showErrorNotification('Ocorreu um erro a cancelar a renda', intl);
        console.log(error);
      },
    },
  );

  const { mutate: bulkDeleteRents } = useMutation(
    (rentIds: string[]) =>
      rentsApi.bulkDeleteRent({ ids: rentIds }).then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        showSuccessNotification('Aluguers eliminados com sucesso', intl);
      },
      onError: (error) => {
        showErrorNotification('Ocorreu um erro a eliminar os aluguers', intl);
        console.log(error);
      },
    },
  );
  const { mutate: markRentAsPayed } = useMutation(
    (rentId: string) => rentsApi.payRent(rentId).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('rent-mark-payed-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error) => {
        showErrorNotification('rent-mark-payed-error-message', intl);
        console.log(error);
      },
    },
  );

  const { mutate: markRentAsUnpayed } = useMutation(
    (rentId: string) => rentsApi.unpayRent(rentId).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('rent-mark-not-payed-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error) => {
        showErrorNotification('rent-mark-not-payed-error-message', intl);
        console.log(error);
      },
    },
  );

  const { mutate: addRentEquipment } = useMutation(
    (data: AddRentEquipmentArg) =>
      rentsApi.addEquipment(data.rentId, data.dto).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('rent-add-equipment-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error) => {
        showErrorNotification('rent-add-equipment-error-message', intl);
        console.log(error);
      },
    },
  );

  const { mutate: removeEquipment } = useMutation(
    (data: RemoveEquipmentArg) =>
      rentsApi.removeEquipment(data.rentId, data.dto).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('rent-remove-equipment-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error) => {
        showErrorNotification('rent-remove-equipment-error-message', intl);
        console.log(error);
      },
    },
  );

  const { mutate: updateRentEquipmentPaymentValue } = useMutation(
    (data: RentCommandArg<UpdateEquipmentPaymentValue>) =>
      rentsApi
        .updateEquipmentPaymentValue(data.rentId, data.dto)
        .then((res) => res.data),

    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        showSuccessNotification(
          'rent-equipment-payment-value-updated-success-message',
          intl,
        );
      },

      onError: () => {
        showErrorNotification(
          'rent-equipment-payment-value-updated-error-message',
          intl,
        );
      },
    },
  );

  const { mutate: updateRentDate } = useMutation(
    (data: RentCommandArg<UpdateRentDateDto>) =>
      rentsApi.updateRentDate(data.rentId, data.dto).then((res) => res.data),
    {
      onSuccess: () => {
        showSuccessNotification('rent-update-date-success-message', intl);
        queryClient.invalidateQueries(queryKey);
      },
      onError: () => {
        showErrorNotification('rent-update-date-error-message', intl);
      },
    },
  );

  const { mutate: updateRentCustomerOrAddress } = useMutation(
    (data: RentCommandArg<UpdateRentCustomerOrAddressDto>) =>
      rentsApi
        .updateRentCustomerOrAddress(data.rentId, data.dto)
        .then((res) => res.data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        showSuccessNotification('rent-update-success-message', intl);
      },
      onError: () => {
        showErrorNotification('rent-update-error-message', intl);
      },
    },
  );

  function openRent(rentId: string) {
    history.push(`/apps/cockpit/rents/${rentId}`);
  }

  return {
    rentsQuery,
    createRent,
    startRent,
    endRent,
    reserveRent,
    cancelRent,
    markRentAsPayed,
    markRentAsUnpayed,
    addRentEquipment,
    openRent,
    removeEquipment,
    updateRentDate,
    updateRentCustomerOrAddress,
    updateRentEquipmentPaymentValue,
    bulkDeleteRents,
  };
}

export interface UseAssetAvailabilityResult {
  isAvailable(date: Dayjs): boolean;
  isAvailableEndDate(date: Dayjs, startDate: Dayjs): boolean;
  isAvailableStartDate(date: Dayjs, startDate: Dayjs): boolean;

  ready: boolean;
}

export function useAssetAvailability(
  currentRentId: string,
  serialNumbers: number[],
): UseAssetAvailabilityResult {
  const { rentsQuery } = useRents();

  function isAvailable(date: Dayjs) {
    if (!rentsQuery.data) {
      return false;
    }

    const assetRents = rentsQuery.data
      .filter((rent) => rent.id !== currentRentId)
      .filter(
        (rent) =>
          !!rent.rentLines.find((line) =>
            serialNumbers.includes(Number(line.asset.serialNumber)),
          ),
      );
    for (const rent of assetRents) {
      if (
        date.isBetween(rent.startDate, rent.endDate, 'days') ||
        date.isSame(rent.startDate, 'days') ||
        date.isSame(rent.endDate, 'days')
      ) {
        return false;
      }
    }

    return true;
  }

  function isAvailableEndDate(current: Dayjs, selectedStartDate: Dayjs) {
    if (!rentsQuery.data) {
      return false;
    }
    const assetRents = rentsQuery.data
      .filter((rent) => rent.id !== currentRentId)
      .filter(
        (rent) =>
          !!rent.rentLines.find((line) =>
            serialNumbers.includes(Number(line.asset.serialNumber)),
          ),
      );

    // The rents come from the api sorted ascendently by startDate
    const startRentDates: string[] = assetRents
      .sort((rentA, rentB) => {
        if (dayjs(rentA.startDate).isAfter(rentB.startDate)) {
          return 1;
        }

        if (dayjs(rentA.startDate).isBefore(rentB.startDate)) {
          return -1;
        }

        return 0;
      })
      .map((rent) => rent.startDate);

    const closestNextStartDay = startRentDates.find((date) =>
      selectedStartDate.isBefore(date),
    );

    if (!closestNextStartDay) {
      return true;
    }
    return current.isBefore(closestNextStartDay, 'days');
  }

  function isAvailableStartDate(current: Dayjs, selectedEndDate: Dayjs) {
    if (!rentsQuery.data) {
      return false;
    }
    console.log('cakked');
    const assetRents = rentsQuery.data
      .filter((rent) => rent.id !== currentRentId)
      .filter(
        (rent) =>
          !!rent.rentLines.find((line) =>
            serialNumbers.includes(Number(line.asset.serialNumber)),
          ),
      );

    // Sort the rents  desc by rent end date
    const endRentsDate: string[] = assetRents
      .sort((rentA, rentB) => {
        if (dayjs(rentA.endDate).isAfter(rentB.endDate)) {
          return -1;
        }

        if (dayjs(rentA.endDate).isBefore(rentB.endDate)) {
          return 1;
        }

        return 0;
      })
      .map((rent) => rent.endDate);

    const closestNextEndDate = endRentsDate.find((date) =>
      selectedEndDate.isAfter(date),
    );

    if (!closestNextEndDate) {
      return true;
    }
    return current.isAfter(closestNextEndDate, 'days');
  }

  return {
    ready: !!rentsQuery.data,
    isAvailable,
    isAvailableEndDate: isAvailableEndDate,
    isAvailableStartDate,
  };
}
